import { useTranslation } from 'react-i18next';

import { type ContactFormValues } from './useContactForm';
import type useToggle from '@/shared/hooks/UseToggle';

import { trpc } from '@/config/trpc';

import { useCreateContact, useUpdateContact } from '@/mutations';
import useNotification from '@/shared/hooks/UseNotification';

import AddEditContactForm from './AddEditContactForm';
import KpcModal from '@/shared/components/2.0/layout/KpcModal';

type Props = {
  contactId?: string;
  toggle: ReturnType<typeof useToggle>;
};

const AddEditContactModal: React.FC<Props> = ({ toggle, contactId }) => {
  const { sendNotification } = useNotification();
  const { data: currentContact } = trpc.contact.byId.useQuery(contactId as string, {
    enabled: !!contactId,
  });
  const { t } = useTranslation();

  const handleSuccess = (
    id: string,
    message: 'typeSuccessfullyUpdated' | 'typeSuccessfullyAdded',
  ) => {
    sendNotification({
      header: t('success'),
      message: t(`${message}`, { type: t('contact') }),
      onSuccessRoute: `/contact/${id}`,
      variant: 'success',
    });
    toggle.hide();
  };

  const { mutate: createContact, isLoading: createContactIsLoading } = useCreateContact();
  const { mutate: updateContact, isLoading: updateContactIsLoading } = useUpdateContact();

  const submit = (values: ContactFormValues) => {
    const { addressLine, city, country, postalCode, ...rest } = values;

    const baseReq = {
      ...rest,
      address: {
        addressLine,
        city,
        country,
        postalCode,
      },
    };

    if (contactId) {
      updateContact(
        {
          ...baseReq,
          contactId,
        },
        { onSuccess: ({ id }) => handleSuccess(id, 'typeSuccessfullyUpdated') },
      );
      return;
    }

    createContact(baseReq, { onSuccess: ({ id }) => handleSuccess(id, 'typeSuccessfullyAdded') });
  };

  return (
    toggle.value && (
      <KpcModal toggle={toggle} title={t('addType', { type: t('contact') })}>
        <AddEditContactForm
          contact={currentContact}
          isSubmitting={createContactIsLoading || updateContactIsLoading}
          onCancel={toggle.hide}
          onSubmit={submit}
        />
      </KpcModal>
    )
  );
};

export default AddEditContactModal;
